import { useEffect, useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, api, errorText } from '../lib/api'
import { keys } from '../lib/queries'
import { formatRelative } from '../lib/format'
import type { DeliverySettings, SettingsSource } from '../lib/types'
import { Button, Field, Input, Section, Select, Spinner, useToast } from '../components/ui'

/**
 * Email and Slack credentials, editable from the browser.
 *
 * These began as environment variables, which is the wrong place for the
 * person who has to fix delivery in the evening: it means editing a compose
 * file and having shell access. The environment still works and still wins
 * where nothing is saved here, so an existing deployment is untouched — the
 * form says which fields it is overriding.
 *
 * Secrets go one way. A saved password is never sent back, so these forms show
 * whether one is set and nothing more.
 */

export function EmailSettingsSection() {
  const settings = useSettings()
  const toast = useToast()
  const save = useSave(api.saveEmailSettings, 'Email settings saved')

  const current = settings.data?.email
  const [form, setForm] = useState(emptyEmail)
  const [password, setPassword] = useState('')

  // The server is the source of truth for everything except the password box,
  // which starts empty because a stored secret is never sent back.
  useEffect(() => {
    if (!current) return
    setForm({
      host: current.host,
      port: current.port ? String(current.port) : '',
      username: current.username,
      from: current.from,
      fromName: current.fromName,
      security: current.security || 'starttls',
    })
    setPassword('')
  }, [settings.dataUpdatedAt])

  const testEmail = useMutation({
    mutationFn: api.testEmail,
    onSuccess: (r) => toast.show(r.status),
    onError: (e) => toast.show('Email test failed', { tone: 'error', detail: errorText(e) }),
  })

  if (!settings.data || !current) {
    return (
      <Section title="Email">
        <Spinner className="text-[var(--ink-3)]" />
      </Section>
    )
  }

  const err = save.error instanceof ApiError ? save.error.fields : undefined

  function submit() {
    save.mutate({
      host: form.host,
      port: form.port.trim() === '' ? 0 : Number(form.port),
      username: form.username,
      from: form.from,
      fromName: form.fromName,
      security: form.security,
      // Only send a password when one was typed, so saving the port does not
      // wipe the stored secret.
      ...(password.trim() === '' ? {} : { password }),
    })
  }

  return (
    <Section
      title="Email"
      hint="Any SMTP server: company mail, a Gmail app password, or a transactional provider."
    >
      <div className="grid gap-3 sm:grid-cols-[1fr_100px]">
        <Field label="Server" htmlFor="smtp-host" error={err?.host}
          hint={sourceHint(current.source.host, 'SMTP_HOST')}>
          <Input
            id="smtp-host" value={form.host} placeholder="smtp.example.com"
            onChange={(e) => setForm({ ...form, host: e.target.value })}
          />
        </Field>
        <Field label="Port" htmlFor="smtp-port" error={err?.port}>
          <Input
            id="smtp-port" inputMode="numeric" value={form.port} placeholder="587"
            onChange={(e) => setForm({ ...form, port: e.target.value.replace(/\D/g, '') })}
          />
        </Field>
      </div>

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <Field label="Username" htmlFor="smtp-user" error={err?.username}
          hint={sourceHint(current.source.username, 'SMTP_USERNAME')}>
          <Input
            id="smtp-user" autoComplete="off" value={form.username}
            onChange={(e) => setForm({ ...form, username: e.target.value })}
          />
        </Field>
        <Field
          label="Password" htmlFor="smtp-pass" error={err?.password}
          hint={
            password.trim() !== ''
              ? 'Will be saved when you save this section'
              : current.hasPassword
                ? 'A password is saved. Type to replace it, or clear the username to drop it.'
                : sourceHint(current.source.password, 'SMTP_PASSWORD') ?? 'No password set'
          }
        >
          <Input
            id="smtp-pass" type="password" autoComplete="new-password" value={password}
            placeholder={current.hasPassword ? '••••••••' : ''}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
      </div>

      <div className="mt-3 grid gap-3 sm:grid-cols-[1fr_1fr_140px]">
        <Field label="From address" htmlFor="smtp-from" error={err?.from}
          hint={sourceHint(current.source.from, 'SMTP_FROM')}>
          <Input
            id="smtp-from" type="email" value={form.from} placeholder="tasks@example.com"
            onChange={(e) => setForm({ ...form, from: e.target.value })}
          />
        </Field>
        <Field label="From name" htmlFor="smtp-from-name" error={err?.fromName}>
          <Input
            id="smtp-from-name" value={form.fromName} placeholder="artsol tasks"
            onChange={(e) => setForm({ ...form, fromName: e.target.value })}
          />
        </Field>
        <Field label="Encryption" htmlFor="smtp-security" error={err?.security}>
          <Select
            id="smtp-security" value={form.security}
            onChange={(e) => setForm({ ...form, security: e.target.value })}
          >
            <option value="starttls">STARTTLS</option>
            <option value="implicit">TLS</option>
            <option value="none">None</option>
          </Select>
        </Field>
      </div>

      <FormFooter
        settings={settings.data}
        saving={save.isPending}
        onSave={submit}
        error={save.error}
        test={
          current.configured && (
            <Button size="sm" onClick={() => testEmail.mutate()} loading={testEmail.isPending}>
              Send test email
            </Button>
          )
        }
      />
    </Section>
  )
}

export function SlackSettingsSection() {
  const settings = useSettings()
  const toast = useToast()
  const save = useSave(api.saveSlackSettings, 'Slack settings saved')

  const current = settings.data?.slack
  const [botToken, setBotToken] = useState('')
  const [clientId, setClientId] = useState('')
  const [clientSecret, setClientSecret] = useState('')
  const [signingSecret, setSigningSecret] = useState('')

  useEffect(() => {
    if (!current) return
    setClientId(current.clientId)
    setBotToken('')
    setClientSecret('')
    setSigningSecret('')
  }, [settings.dataUpdatedAt])

  const testSlack = useMutation({
    mutationFn: api.testSlack,
    onSuccess: (r) => toast.show(r.status),
    onError: (e) => toast.show('Slack test failed', { tone: 'error', detail: errorText(e) }),
  })

  if (!settings.data || !current) {
    return (
      <Section title="Slack">
        <Spinner className="text-[var(--ink-3)]" />
      </Section>
    )
  }

  const err = save.error instanceof ApiError ? save.error.fields : undefined

  return (
    <Section
      title="Slack"
      hint="The bot token posts messages. The client ID and secret are only needed if people should be able to link their own Slack accounts."
    >
      <Field
        label="Bot token" htmlFor="slack-bot" error={err?.botToken}
        hint={
          botToken.trim() !== ''
            ? 'Will be saved when you save this section'
            : current.hasBotToken
              ? `A token is saved${sourceSuffix(current.source.botToken)}. Type to replace it.`
              : 'Create a Slack app, add the chat:write scope, install it, and paste the bot token here'
        }
      >
        <Input
          id="slack-bot" type="password" autoComplete="off" value={botToken}
          placeholder={current.hasBotToken ? '••••••••' : 'xoxb-…'}
          onChange={(e) => setBotToken(e.target.value)}
        />
      </Field>

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <Field label="Client ID" htmlFor="slack-id" error={err?.clientId}
          hint={sourceHint(current.source.clientId, 'SLACK_CLIENT_ID')}>
          <Input
            id="slack-id" value={clientId} placeholder="1234567890.1234567890"
            onChange={(e) => setClientId(e.target.value)}
          />
        </Field>
        <Field
          label="Client secret" htmlFor="slack-secret" error={err?.clientSecret}
          hint={
            clientSecret.trim() !== ''
              ? 'Will be saved when you save this section'
              : current.hasClientSecret
                ? `Saved${sourceSuffix(current.source.clientSecret)}`
                : 'Needed for account linking'
          }
        >
          <Input
            id="slack-secret" type="password" autoComplete="off" value={clientSecret}
            placeholder={current.hasClientSecret ? '••••••••' : ''}
            onChange={(e) => setClientSecret(e.target.value)}
          />
        </Field>
      </div>

      <div className="mt-3">
        <Field
          label="Signing secret" htmlFor="slack-signing" error={err?.signingSecret}
          hint={
            current.hasSigningSecret && signingSecret.trim() === ''
              ? `Saved${sourceSuffix(current.source.signingSecret)}`
              : 'Optional today — reserved for verifying requests Slack sends back'
          }
        >
          <Input
            id="slack-signing" type="password" autoComplete="off" value={signingSecret}
            placeholder={current.hasSigningSecret ? '••••••••' : ''}
            onChange={(e) => setSigningSecret(e.target.value)}
          />
        </Field>
      </div>

      <FormFooter
        settings={settings.data}
        saving={save.isPending}
        error={save.error}
        onSave={() =>
          save.mutate({
            clientId,
            ...(botToken.trim() === '' ? {} : { botToken }),
            ...(clientSecret.trim() === '' ? {} : { clientSecret }),
            ...(signingSecret.trim() === '' ? {} : { signingSecret }),
          })
        }
        test={
          current.botConfigured && (
            <Button size="sm" onClick={() => testSlack.mutate()} loading={testSlack.isPending}>
              Send test message
            </Button>
          )
        }
      />
    </Section>
  )
}

// ------------------------------------------------------------------ shared

const emptyEmail = {
  host: '', port: '', username: '', from: '', fromName: '', security: 'starttls',
}

function useSettings() {
  return useQuery({ queryKey: keys.adminSettings(), queryFn: api.settings })
}

function useSave<T>(fn: (body: T) => Promise<DeliverySettings>, message: string) {
  const queryClient = useQueryClient()
  const toast = useToast()
  return useMutation({
    mutationFn: fn,
    onSuccess: (data) => {
      queryClient.setQueryData(keys.adminSettings(), data)
      // The health strip reads the same live values, so it must not lag.
      queryClient.invalidateQueries({ queryKey: keys.adminStatus() })
      toast.show(message)
    },
  })
}

function FormFooter({
  settings, saving, onSave, error, test,
}: {
  settings: DeliverySettings
  saving: boolean
  onSave: () => void
  error: unknown
  test?: ReactNode
}) {
  const general =
    error instanceof ApiError && Object.keys(error.fields ?? {}).length === 0
      ? error.message
      : error instanceof Error && !(error instanceof ApiError)
        ? error.message
        : null

  return (
    <>
      {general && <p className="mt-3 text-[12.5px] text-[var(--danger)]">{general}</p>}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Button variant="primary" onClick={onSave} loading={saving}>
          Save
        </Button>
        {test}
        {settings.updatedBy && settings.updatedAt && (
          // One row holds both sections, so this is about the pair of them —
          // saying "last changed" under Slack alone would misread as Slack.
          <span className="ml-auto text-[11.5px] text-[var(--ink-3)]">
            Delivery settings last changed by {settings.updatedBy.name}{' '}
            {formatRelative(settings.updatedAt)}
          </span>
        )}
      </div>
    </>
  )
}

/**
 * Says when a value is coming from the container rather than this form, so an
 * empty box does not read as "nothing is set".
 */
function sourceHint(source: SettingsSource | undefined, variable: string): string | undefined {
  return source === 'environment' ? `Currently set by ${variable}; saving here overrides it` : undefined
}

function sourceSuffix(source: SettingsSource | undefined): string {
  return source === 'environment' ? ' by the environment' : ''
}

